import React, { useEffect, useMemo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { useWizard } from 'ui-components/components/wizard/context';

import { BlackBoxSection } from './black-box-section';
import style from './style.module.less';
import { US } from '../../../../../constants/country-codes';
import fsuiStyles from '../../../../../financial-services-ui/fsui-styles.module.less';
import { useHumanScript } from '../../../../../hooks/use-human-script';
import { useOnce } from '../../../../../hooks/use-once';
import { TaxIdentifierSection } from '../../../../../pages/settings/payment-and-subscription/components/tax-identifier-section';
import { LogService } from '../../../../../services/log-service';
import Global from '../../../../../store/global-selectors';
import { useRequirementsChecklist } from '../../../../hooks/use-requirements-checklist';
import { messages } from '../../../../messages';
import { FORM_REQUIREMENTS, PAYMENT_AND_LOCATION_CREDIT_CARD_FORMS } from '../../../../utilities/constants';
import { BUSINESS, PERSONAL } from '../../../constants';
import { BillingAndPickup } from '../../components/billing-and-pickup';
import { RequirementGuard } from '../../components/requirement-guard';
import { StampsCredentialsPreview } from '../../components/stamps-account-credentials/stamps-credentials-preview';
import { WalletNickname } from '../../components/wallet-nickname';
import { WalletRegistrationCreditCard } from '../../components/wallet-registration-credit-card';
import { WalletRegistrationErrorBanner } from '../../components/wallet-registration-error-banner';
import { useWalletRegistrationErrorContext } from '../../contexts/wallet-registration-error-context';
import { useWalletRegistrationFormContext } from '../../contexts/wallet-registration-form-context';
import { useWalletRegistrationModalContext } from '../../contexts/wallet-registration-modal-context';
import commonStyles from '../../style.module.less';

const LEFT_COLUMN_REQUIREMENTS = [FORM_REQUIREMENTS.BILLING_ADDRESS];
const RIGHT_COLUMN_REQUIREMENTS = [FORM_REQUIREMENTS.CREDIT_CARD, FORM_REQUIREMENTS.NICKNAME];

export const PaymentAndLocationStep = () => {
  const { requiredForms } = useWalletRegistrationModalContext();
  const { setIsPaymentComplete } = useWalletRegistrationFormContext();
  const { errors } = useWalletRegistrationErrorContext();
  const { updateWalletForm, walletForm } = useWalletRegistrationFormContext();

  const currentRequiredForms = useMemo(() => {
    return requiredForms.filter((form) => PAYMENT_AND_LOCATION_CREDIT_CARD_FORMS.includes(form));
  }, [requiredForms]);
  const { checklistCompleted, updateRequirementStatus } = useRequirementsChecklist(currentRequiredForms);
  const { setCanProgress } = useWizard();

  useEffect(() => {
    setCanProgress(checklistCompleted);
  }, [setCanProgress, checklistCompleted, errors]);

  const markCreditCardFormComplete = useCallback(
    (id, value) => {
      setIsPaymentComplete(value);
      updateRequirementStatus(id, value);
    },
    [setIsPaymentComplete, updateRequirementStatus],
  );

  const radioOptions = [
    {
      id: BUSINESS,
      translation: messages.businessRadioButton,
    },
    {
      id: PERSONAL,
      translation: messages.personalRadioButton,
    },
  ];

  useOnce(() => {
    LogService.logInfo('Deprecated PaymentAndLocationStep mounted');
  });

  const homeCountry = useSelector(Global.selectSellerHomeCountry);
  const shouldShowRadios = homeCountry === US;

  // side effects
  useHumanScript();

  return (
    <div className={commonStyles['step-content']}>
      <WalletRegistrationErrorBanner />

      <BlackBoxSection updateRequirementStatus={updateRequirementStatus} />

      <div className={commonStyles.header}>
        <FormattedMessage {...messages.paymentTabHeader} />
      </div>
      <div className={fsuiStyles['flex-container']}>
        <RequirementGuard requirements={RIGHT_COLUMN_REQUIREMENTS}>
          <div className={fsuiStyles['credit-wrapper']}>
            {shouldShowRadios ? (
              <>
                <div className={style.subheader}>
                  <FormattedMessage {...messages.accountType} />
                </div>
                <RadioCheckList className={style['radio-button-section']}>
                  {radioOptions.map((option) => (
                    <RadioCheckListItem
                      key={option.id}
                      className={style['radio-item']}
                      isChecked={option.id === walletForm?.[FORM_REQUIREMENTS.ACCOUNT_TYPE]}
                      onChecked={() => updateWalletForm([FORM_REQUIREMENTS.ACCOUNT_TYPE], option.id)}
                    >
                      <FormattedMessage {...option.translation} />
                    </RadioCheckListItem>
                  ))}
                </RadioCheckList>
              </>
            ) : null}
            <RequirementGuard requirements={FORM_REQUIREMENTS.STAMPS_CREDENTIALS}>
              <StampsCredentialsPreview />
            </RequirementGuard>
            <RequirementGuard requirements={FORM_REQUIREMENTS.CREDIT_CARD}>
              <WalletRegistrationCreditCard markFormComplete={markCreditCardFormComplete} />
            </RequirementGuard>
            <RequirementGuard requirements={FORM_REQUIREMENTS.NICKNAME}>
              <WalletNickname markFormComplete={updateRequirementStatus} />
            </RequirementGuard>

            <RequirementGuard requirements={FORM_REQUIREMENTS.TAX_ID_INFORMATION}>
              <TaxIdentifierSection />
            </RequirementGuard>
          </div>
        </RequirementGuard>
        <RequirementGuard requirements={LEFT_COLUMN_REQUIREMENTS}>
          <div className={fsuiStyles['billing-wrapper']}>
            <BillingAndPickup accountType={walletForm?.[FORM_REQUIREMENTS.ACCOUNT_TYPE]} markFormComplete={updateRequirementStatus} />
          </div>
        </RequirementGuard>
      </div>
    </div>
  );
};
